'use client';

import React, { useEffect, useState } from 'react';
import { rentalService, RentalInventoryItem } from '@/services/rental-service';
import Link from 'next/link';

export default function SiteVitrine() {
  const [items, setItems] = useState<RentalInventoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadItems() {
      try {
        const inventory = await rentalService.getInventory();
        // Show available and reserved items, limit to 6
        setItems(inventory.filter(i => i.status === 'disponivel' || i.status === 'reservado').slice(0, 6));
      } catch (error) {
        console.error("Erro ao carregar vitrine:", error);
      } finally {
        setLoading(false);
      }
    }
    loadItems();
  }, []);

  if (loading) {
    return (
      <section className="py-xl bg-surface-container-lowest animate-pulse">
        <div className="max-w-7xl mx-auto px-lg">
          <div className="h-10 w-48 bg-outline-variant/20 rounded mb-md"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-lg">
            {[1, 2, 3].map(i => <div key={i} className="aspect-[3/4] bg-outline-variant/10 rounded-2xl"></div>)}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="aluguel" className="py-xl bg-surface-container-lowest">
      <div className="max-w-7xl mx-auto px-lg">
        <div className="flex flex-col md:flex-row justify-between items-end mb-xl gap-6">
          <div>
            <span className="text-label-caps text-primary tracking-[0.2em] font-bold">CURADORIA EXCLUSIVA</span>
            <h2 className="text-h2 text-on-surface mt-2">Vitrine Ativa</h2>
            <p className="text-body-md text-secondary mt-1">Peças únicas para momentos extraordinários.</p>
          </div>
          <Link 
            href="/login" 
            className="text-button text-primary hover:underline flex items-center gap-2 group"
          >
            Ver Catálogo Completo
            <span className="material-symbols-outlined transition-transform group-hover:translate-x-1">arrow_right_alt</span>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-gutter">
          {items.map((item) => (
            <div key={item.id} className="bg-white rounded-3xl overflow-hidden ambient-shadow flex flex-col group hover:-translate-y-2 transition-all duration-500 border border-outline-variant/10">
              <div className="relative aspect-[3/4] overflow-hidden bg-surface-container-low">
                {item.image_url ? (
                  <img 
                    src={item.image_url} 
                    alt={item.name} 
                    className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ${item.status === 'reservado' ? 'grayscale opacity-70' : ''}`} 
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-secondary/20">
                    <span className="material-symbols-outlined text-6xl">texture</span>
                  </div>
                )}
                
                {/* Status Badge */}
                <div className="absolute top-md left-md flex flex-col gap-2">
                  {item.status === 'reservado' && (
                    <div className="px-md py-xs bg-error text-white rounded-full text-label-caps text-[9px] font-black shadow-lg animate-fade-in uppercase">
                      Indisponível no momento
                    </div>
                  )}
                  {item.categories && item.categories.length > 0 && (
                    <div className="px-md py-xs bg-white/80 backdrop-blur-md rounded-full text-label-caps text-[10px] text-on-surface font-black w-fit">
                      {item.categories[0].name}
                    </div>
                  )}
                </div>
              </div>
              <div className="p-lg flex flex-col flex-grow">
                <div className="flex justify-between items-start mb-md">
                  <div>
                    <h3 className="text-h3 text-on-surface text-xl">{item.name}</h3>
                    <p className="text-body-md text-secondary text-sm mt-1">{item.description || 'Peça Exclusiva'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-black text-secondary/40 uppercase tracking-widest">ALUGUEL</p>
                    <span className="text-xl font-bold text-primary">R$ {item.daily_rate.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
                
                <a 
                  href={`https://wa.me/5579988086893?text=Olá Eliz! Vi o vestido ${item.name} no site e gostaria de saber ${item.status === 'reservado' ? 'quando ele estará disponível para aluguel.' : 'mais sobre o aluguel.'}`}
                  target="_blank"
                  className={`mt-auto w-full py-md rounded-xl font-button text-button transition-all text-center flex items-center justify-center gap-2 group ${
                    item.status === 'reservado' 
                    ? 'bg-surface-container-high text-secondary hover:bg-secondary hover:text-white' 
                    : 'bg-surface-container-low text-primary hover:bg-primary hover:text-on-primary'
                  }`}
                >
                  {item.status === 'reservado' ? 'Consultar Disponibilidade' : 'Reservar Peça'}
                  <span className="material-symbols-outlined text-[18px] opacity-0 group-hover:opacity-100 transition-all">
                    {item.status === 'reservado' ? 'event_available' : 'local_mall'}
                  </span>
                </a>
              </div>
            </div>
          ))}
        </div>

        {items.length === 0 && (
          <div className="text-center py-xl border-2 border-dashed border-outline-variant/30 rounded-3xl">
            <p className="text-secondary font-medium italic">Nossas peças estão em manutenção. Volte em breve!</p>
          </div>
        )}
      </div>
    </section>
  );
}
